import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { SystemCard } from "../WidgetPages/SystemCard/SystemCard";
import StrictModeDroppable from "../WidgetPages/EditWidget/StrictModeDroppable";

/**
 * Drag-and-drop section for reordering widgets.
 * Only used inside the widgets_configuration drawer.
 *
 * Reads from formData to build the ordered list, and calls
 * onReorder with updated formData when items are dragged.
 */
const WidgetDragDropSection = ({ formData, onReorder, disabled }) => {
  const [draggableList, setDraggableList] = useState([]);

  useEffect(() => {
    const list = Object.entries(formData || {})
      .filter(([, val]) =>
        disabled
          ? val?.display && val?.position > 0
          : typeof val?.display === "boolean"
      )
      .sort(
        (a, b) =>
          (Number(a[1]?.position) || Number.MAX_SAFE_INTEGER) -
          (Number(b[1]?.position) || Number.MAX_SAFE_INTEGER)
      )
      .map(([key]) => key);

    setDraggableList(list);
  }, [disabled, formData]);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(draggableList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedFormData = { ...formData };
    items.forEach((item, index) => {
      updatedFormData[item] = {
        ...updatedFormData[item],
        position: index + 1,
      };
    });

    setDraggableList(items);
    onReorder(updatedFormData);
  };

  const renderItem = (item, index) => (
    <Draggable key={item} draggableId={item} index={index} isDragDisabled={disabled}>
      {(provided) => (
        <div
          className="widget-drag-item"
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <SystemCard
            active={Boolean(formData[item]?.display)}
            onActiveChange={(display) =>
              onReorder({
                ...formData,
                [item]: { ...formData[item], display },
              })
            }
            readOnly={disabled}
            showDragHandle
            widgetName={item}
          />
        </div>
      )}
    </Draggable>
  );

  if (draggableList.length === 0) return null;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable droppableId="widgetDrawerDroppable" direction="vertical">
        {(provided) => (
          <div
            className="widgets-view-order-list"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {draggableList.map(renderItem)}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

WidgetDragDropSection.propTypes = {
  formData: PropTypes.object.isRequired,
  onReorder: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default WidgetDragDropSection;
